import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import Dynamic from '@base/Dynamic/Dynamic';
import { TEST_SIGNUP_BANNER } from '@comp/SignupGenericBanner/constant';
import { TEST_SERIES_EVENTS } from '@cont/SeeAll/constants';
import { setTestSeriesReferralInStorage } from '@cont/SeeAll/utils';
import styled from '@emotion/styled';
import useTestSeriesStore from '@stores/test-series';
import { mediaQuery, Spacings } from '@styles/index';
import { getParameterByName } from '@utils/textHelper';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import Header from './Header';
import Sections from './Sections';
import useGoalSubscription from './useGoalSubscription';
import useSkuDetails from './useSkuDetails';

import useTestSeriesEvents from './useTestSeriesEvents';
import useTestSeriesInfo from './useTestSeriesInfo';

const SignupGenericBanner = Dynamic(() =>
  import('@comp/SignupGenericBanner/SignupGenericBanner')
);

const Wrapper = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const StyledSignupBanner = styled(SignupGenericBanner)`
  margin-top: ${Spacings.SPACING_10B};
`;

const TestSeriesListing = ({ fromStore }) => {
  const [isStickyHeaderShown, setIsStickyHeaderShown] = useState(false);
  const { data } = useTestSeriesInfo();
  const { goalUid, isSpecial } = data;

  const { query, asPath } = useRouter();

  const referralCode = getParameterByName('referral_code', asPath);

  useEffect(() => {
    if (referralCode && query.courseUID) {
      setTestSeriesReferralInStorage(query.courseUID, referralCode);
    }
  }, [query.courseUID, referralCode]);

  const { isSubscribed } = useGoalSubscription();
  const {
    isSkuAvailable,
    isExpired,
    isValidating,
    mutate: refectchSku,
    refetchUam,
    isError,
    hasSkuAccess
  } = useSkuDetails(goalUid, query.courseUID);

  const { sendViewedTestSeriesEvent } = useTestSeriesEvents(hasSkuAccess);

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { isValidating: isMyInfoValidating } = useMyInfo();

  const isStoreDetailsShown = () => {
    if (fromStore) {
      if (isError) {
        return false;
      }
      if (isValidating) {
        return true;
      }
      if (!isLoggedIn && isSkuAvailable) {
        return true;
      }
      if (hasSkuAccess) {
        return true; // show sku purchased flow
      }

      if (isSubscribed || isExpired) {
        return false; // Do not show sku section if user has goal subscription or the purchased sku expired
      }

      if (isSkuAvailable) {
        return true; // show sku purchase flow
      }
    }
    if (isMyInfoValidating || isValidating) {
      return true;
    }
    if (hasSkuAccess) {
      return true; // show sku purchased flow
    }

    return false;
  };

  const { isSkuInfoFetchedOnClient, isUamInfoFetchedOnClient } =
    useTestSeriesStore();
  const viewEventFiredRef = useRef(false);

  useEffect(() => {
    if (
      (((!isLoggedIn || isSubscribed || isSpecial) &&
        isSkuInfoFetchedOnClient) ||
        isUamInfoFetchedOnClient) &&
      !viewEventFiredRef.current
    ) {
      viewEventFiredRef.current = true;
      sendViewedTestSeriesEvent(TEST_SERIES_EVENTS.TEST_SERIES_VIEWED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSkuInfoFetchedOnClient, isUamInfoFetchedOnClient]);

  useEffect(() => {
    if (isLoggedIn) {
      refectchSku();
      refetchUam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <Wrapper>
      <Header
        fromStore={isStoreDetailsShown()}
        setIsStickyHeaderShown={setIsStickyHeaderShown}
      />
      <Sections
        fromStore={isStoreDetailsShown()}
        isStickyHeaderShown={isStickyHeaderShown}
      />
      <StyledSignupBanner data={TEST_SIGNUP_BANNER} goalUID={goalUid} />
    </Wrapper>
  );
};

export default TestSeriesListing;
